import React, { createContext, useState, useEffect } from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
}

export interface AuthContextType {
  auth: AuthState;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [auth, setAuth] = useState<AuthState>({
    user: null,
    token: null
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Function to check token validity and refresh if needed
  const checkAndRefreshToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Check if token is expired or about to expire
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${backendUrl}/verify-token`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      // If token is expired or about to expire, refresh it
      if (!response.ok || data.needsRefresh) {
        console.log('Token needs refresh, attempting to refresh...');
        const refreshResponse = await fetch(`${backendUrl}/refresh-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const refreshData = await refreshResponse.json();
        
        if (refreshResponse.ok && refreshData.token) {
          console.log('Token refreshed successfully');
          // Update token in localStorage
          localStorage.setItem('token', refreshData.token);
          
          // Update auth state with new token
          setAuth(prevAuth => ({
            ...prevAuth,
            token: refreshData.token
          }));
          
          // Update auth in localStorage
          const savedAuth = localStorage.getItem('auth');
          if (savedAuth) {
            const parsedAuth = JSON.parse(savedAuth);
            parsedAuth.token = refreshData.token;
            localStorage.setItem('auth', JSON.stringify(parsedAuth));
          }
        } else {
          console.error('Failed to refresh token, logging out');
          // If refresh fails, log the user out
          setAuth({ user: null, token: null });
          localStorage.removeItem('auth');
          localStorage.removeItem('token');
        }
      }
    } catch (error) {
      console.error('Token verification error:', error);
      // On error, consider logging out the user
      setAuth({ user: null, token: null });
      localStorage.removeItem('auth');
      localStorage.removeItem('token');
    }
  };

  useEffect(() => {
    // Check for saved auth data in localStorage
    const savedAuth = localStorage.getItem('auth');
    
    if (savedAuth) {
      try {
        const parsedAuth = JSON.parse(savedAuth);
        setAuth(parsedAuth);
        
        // Check token validity immediately
        checkAndRefreshToken();
      } catch (error) {
        console.error('Failed to parse auth data:', error);
        localStorage.removeItem('auth');
      }
    }
    
    setIsLoading(false);
    
    // Set up interval to check token every 5 minutes
    const tokenCheckInterval = setInterval(checkAndRefreshToken, 5 * 60 * 1000);
    
    // Clean up interval on component unmount
    return () => clearInterval(tokenCheckInterval);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Use the actual backend authentication
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${backendUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
      
      // Get token and user data from response
      const token = data.token;
      const userDetails = data.user;
      
      // Verify that user data exists
      if (!userDetails) {
        throw new Error('User data not provided in login response');
      }
      
      // Create user data from backend response with fallbacks for missing properties
      const userData: User = {
        id: userDetails.id || 'unknown',
        name: userDetails.first_name && userDetails.last_name 
          ? `${userDetails.first_name} ${userDetails.last_name}`
          : email.split('@')[0], // Fallback to email username if name is missing
        email: userDetails.email || email,
        role: userDetails.roles && userDetails.roles.length > 0 ? userDetails.roles[0] : 'user'
      };
      
      const authData = {
        user: userData,
        token
      };
      
      // Save to state and localStorage
      setAuth(authData);
      localStorage.setItem('auth', JSON.stringify(authData));
      localStorage.setItem('token', token); // Store token separately for API calls
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        await fetch(`${backendUrl}/api/auth/signout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local state regardless of API call result
      setAuth({ user: null, token: null });
      localStorage.removeItem('auth');
      localStorage.removeItem('token');
    }
  };

  const value = {
    auth,
    isAuthenticated: !!auth.token,
    isLoading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
